import axios from 'axios';
import { Token } from '../types';

export const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('access_token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

let isRefreshing = false;
let failedQueue: { resolve: (token: string) => void; reject: (err: any) => void }[] = [];

const processQueue = (error: any, token: string | null = null) => {
  failedQueue.forEach((prom) => {
    if (error) prom.reject(error);
    else prom.resolve(token!);
  });
  failedQueue = [];
};

api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;
    
    if (error.response?.status === 401 && !originalRequest._retry) {
      if (isRefreshing) {
        return new Promise<string>((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        }).then((token) => {
          originalRequest.headers.Authorization = `Bearer ${token}`;
          return api(originalRequest);
        });
      }

      originalRequest._retry = true;
      isRefreshing = true;
      
      const refreshToken = localStorage.getItem('refresh_token');
      if (refreshToken) {
        try {
          const response = await axios.post(`${API_BASE_URL}/api/auth/refresh`, null, {
            params: { refresh_token: refreshToken },
          });
          
          const { access_token, refresh_token } = response.data;
          localStorage.setItem('access_token', access_token);
          localStorage.setItem('refresh_token', refresh_token);
          
          processQueue(null, access_token);

          originalRequest.headers.Authorization = `Bearer ${access_token}`;
          return api(originalRequest);
        } catch (refreshError) {
          processQueue(refreshError, null);
          localStorage.removeItem('access_token');
          localStorage.removeItem('refresh_token');
          window.location.href = '/login';
        } finally {
          isRefreshing = false;
        }
      } else {
        isRefreshing = false;
      }
    }
    
    return Promise.reject(error);
  }
);

export const authApi = {
  login: async (username: string, password: string): Promise<Token> => {
    const formData = new URLSearchParams();
    formData.append('username', username);
    formData.append('password', password);
    
    const response = await api.post('/api/auth/login', formData, {
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
    });
    return response.data;
  },
  getMe: async () => {
    const response = await api.get('/api/auth/me');
    return response.data;
  },
};

export const departmentApi = {
  getAll: () => api.get('/api/departments/'),
  getByUnitId: (unitId: string) => api.get(`/api/departments/${unitId}`),
  getTargets: (unitId: string) => api.get(`/api/departments/${unitId}/targets`),
  getRisks: (unitId: string) => api.get(`/api/departments/${unitId}/risks`),
  getActivity: (unitId: string) => api.get(`/api/departments/${unitId}/activity`),
  create: (data: any) => api.post('/api/departments/', data),
  update: (unitId: string, data: any) => api.put(`/api/departments/${unitId}`, data),
  delete: (unitId: string) => api.delete(`/api/departments/${unitId}`),
};

export const riskApi = {
  getAll: (params?: { unit_id?: string; fiscal_year_id?: number; status?: string; erm_level?: string }) =>
    api.get('/api/risks/', { params }),
  getNextId: () => api.get('/api/risks/next-id'),
  getByRiskId: (riskId: string) => api.get(`/api/risks/${riskId}`),
  getByRiskCode: (riskCode: string, fiscalYearId?: number) =>
    api.get(`/api/risks/by-code/${riskCode}`, { params: { fiscal_year_id: fiscalYearId } }),
  create: (data: any) => api.post('/api/risks/', data),
  update: (riskId: string, data: any) => api.put(`/api/risks/${riskId}`, data),
  delete: (riskId: string) => api.delete(`/api/risks/${riskId}`),
  getHeatMap: (params?: { unit_id?: string; fiscal_year_id?: number; erm_level?: string }) =>
    api.get('/api/risks/matrix/heat-map', { params }),
  getDashboard: (params?: { unit_id?: string; fiscal_year_id?: number; erm_level?: string }) =>
    api.get('/api/risks/dashboard/stats', { params }),
  getDashboardAnalytics: (params?: { unit_id?: string; fiscal_year_id?: number; erm_level?: string }) =>
    api.get('/api/risks/dashboard/analytics', { params }),
  getIcAcCoverage: (params?: { unit_id?: string; fiscal_year_id?: number; erm_level?: string }) =>
    api.get('/api/risks/dashboard/ic-ac-coverage', { params }),
  addCause: (riskId: string, data: any) => api.post(`/api/risks/${riskId}/causes`, data),
  updateCause: (causeId: number, data: any) => api.put(`/api/risks/causes/${causeId}`, data),
  deleteCause: (causeId: number) => api.delete(`/api/risks/causes/${causeId}`),
  addIC: (riskId: string, data: any) => api.post(`/api/risks/${riskId}/ic`, data),
  updateIC: (icId: number, data: any) => api.put(`/api/risks/ic/${icId}`, data),
  deleteIC: (icId: number) => api.delete(`/api/risks/ic/${icId}`),
  addAC: (riskId: string, data: any) => api.post(`/api/risks/${riskId}/ac`, data),
  updateAC: (acId: number, data: any) => api.put(`/api/risks/ac/${acId}`, data),
  deleteAC: (acId: number) => api.delete(`/api/risks/ac/${acId}`),
  addKRI: (riskId: string, data: any) => api.post(`/api/risks/${riskId}/kri`, data),
  updateKRI: (kriId: number, data: any) => api.put(`/api/risks/kri/${kriId}`, data),
  deleteKRI: (kriId: number) => api.delete(`/api/risks/kri/${kriId}`),
  pickCause: (riskId: string, causeMasterId: number) =>
    api.post(`/api/risks/${riskId}/causes/pick`, { cause_master_id: causeMasterId }),
  pickIC: (riskId: string, icMasterId: number) =>
    api.post(`/api/risks/${riskId}/ic/pick`, { ic_master_id: icMasterId }),
  pickAC: (riskId: string, acMasterId: number) =>
    api.post(`/api/risks/${riskId}/ac/pick`, { ac_master_id: acMasterId }),
  pickKRI: (riskId: string, kriMasterId: number) =>
    api.post(`/api/risks/${riskId}/kri/pick`, { kri_master_id: kriMasterId }),
  addComment: (riskId: string, data: any) => api.post(`/api/risks/${riskId}/comment`, data),
  addMonitoring: (riskId: string, data: any) => api.post(`/api/risks/${riskId}/monitoring`, data),
  copyRisk: (data: { source_risk_id: string; target_fiscal_year_id: number }) =>
    api.post('/api/risks/copy', data),
  registerErm: (riskId: string) => api.post(`/api/risks/${riskId}/erm`),
  unregisterErm: (riskId: string) => api.delete(`/api/risks/${riskId}/erm`),
  requestDelete: (riskId: string) => api.delete(`/api/risks/${riskId}`),
  approveDelete: (riskId: string) => api.post(`/api/risks/${riskId}/approve-delete`),
  rejectDelete: (riskId: string) => api.post(`/api/risks/${riskId}/reject-delete`),
  getPendingDeletions: () => api.get('/api/risks/pending-deletions'),
};

export const masterRiskApi = {
  getAll: (params?: { risk_type_id?: number; strategy_id?: number }) =>
    api.get('/api/risks/master', { params }),
  create: (data: any) => api.post('/api/risks/master', data),
  update: (riskCode: string, data: any) => api.put(`/api/risks/master/${riskCode}`, data),
  delete: (riskCode: string) => api.delete(`/api/risks/master/${riskCode}`),
  getRisks: (riskCode: string) => api.get(`/api/risks/master/${riskCode}/risks`),
  getDetail: (riskCode: string) => api.get(`/api/risks/master/${riskCode}/detail`),
  addCause: (riskCode: string, data: any) => api.post(`/api/risks/master/${riskCode}/causes`, data),
  updateCause: (id: number, data: any) => api.put(`/api/risks/master/causes/${id}`, data),
  deleteCause: (id: number) => api.delete(`/api/risks/master/causes/${id}`),
  addIC: (riskCode: string, data: any) => api.post(`/api/risks/master/${riskCode}/ic`, data),
  updateIC: (id: number, data: any) => api.put(`/api/risks/master/ic/${id}`, data),
  deleteIC: (id: number) => api.delete(`/api/risks/master/ic/${id}`),
  addAC: (riskCode: string, data: any) => api.post(`/api/risks/master/${riskCode}/ac`, data),
  updateAC: (id: number, data: any) => api.put(`/api/risks/master/ac/${id}`, data),
  deleteAC: (id: number) => api.delete(`/api/risks/master/ac/${id}`),
  addKRI: (riskCode: string, data: any) => api.post(`/api/risks/master/${riskCode}/kri`, data),
  updateKRI: (id: number, data: any) => api.put(`/api/risks/master/kri/${id}`, data),
  deleteKRI: (id: number) => api.delete(`/api/risks/master/kri/${id}`),
};

export const kriApi = {
  getAll: (params?: { risk_id?: string }) =>
    api.get('/api/kri', { params }),
  getById: (id: number) => api.get(`/api/kri/${id}`),
  create: (data: any) => api.post('/api/kri', data),
  update: (id: number, data: any) => api.put(`/api/kri/${id}`, data),
};

export const importApi = {
  importExcel: (file: File) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/api/import/excel', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
};

export const auditApi = {
  getAll: (params?: { user_id?: number; table_name?: string; action?: string }) =>
    api.get('/api/audit', { params }),
};

export const uploadApi = {
  uploadEvidence: (file: File, fiscalYearId?: number) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/api/uploads/evidence', formData, {
      params: fiscalYearId ? { fiscal_year_id: fiscalYearId } : {},
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  deleteEvidence: (id: number) => {
    return api.delete(`/api/uploads/evidence/${id}`);
  },
};

export const reportApi = {
  exportDoc3: (params: { unit_id?: string; fiscal_year_id: number }) => {
    return api.get('/api/reports/doc3', {
      params,
      responseType: 'blob',
    });
  },
  exportDoc4: (params: { unit_id?: string; fiscal_year_id: number }) => {
    return api.get('/api/reports/doc4', {
      params,
      responseType: 'blob',
    });
  },
  exportDoc3Pdf: (params: { unit_id?: string; fiscal_year_id: number }) => {
    return api.get('/api/reports/doc3/pdf', {
      params,
      responseType: 'blob',
    });
  },
  exportDoc4Pdf: (params: { unit_id?: string; fiscal_year_id: number }) => {
    return api.get('/api/reports/doc4/pdf', {
      params,
      responseType: 'blob',
    });
  },
  exportDoc3Word: (params: { unit_id?: string; fiscal_year_id: number }) => {
    return api.get('/api/reports/doc3/word', {
      params,
      responseType: 'blob',
    });
  },
  exportDoc4Word: (params: { unit_id?: string; fiscal_year_id: number }) => {
    return api.get('/api/reports/doc4/word', {
      params,
      responseType: 'blob',
    });
  },
  exportDoc3ExcelZip: (params: { unit_id?: string; fiscal_year_id: number }) => {
    return api.get('/api/reports/doc3/excel-zip', {
      params,
      responseType: 'blob',
    });
  },
  exportDoc4ExcelZip: (params: { unit_id?: string; fiscal_year_id: number }) => {
    return api.get('/api/reports/doc4/excel-zip', {
      params,
      responseType: 'blob',
    });
  },
};

export const userApi = {
  getAll: () => api.get('/api/users'),
  getById: (id: number) => api.get(`/api/users/${id}`),
  create: (data: any) => api.post('/api/users', data),
  update: (id: number, data: any) => api.put(`/api/users/${id}`, data),
  delete: (id: number) => api.delete(`/api/users/${id}`),
};

export const roleApi = {
  getAll: () => api.get('/api/roles'),
  getById: (id: number) => api.get(`/api/roles/${id}`),
  create: (data: any) => api.post('/api/roles', data),
  update: (id: number, data: any) => api.put(`/api/roles/${id}`, data),
  delete: (id: number) => api.delete(`/api/roles/${id}`),
  assignUser: (roleId: number, userId: number) => api.post(`/api/roles/${roleId}/users/${userId}`),
  removeUser: (roleId: number, userId: number) => api.delete(`/api/roles/${roleId}/users/${userId}`),
};

export const lookupApi = {
  getRiskTypes: () => api.get('/api/lookups/risk-types'),
  getStrategies: () => api.get('/api/lookups/strategies'),
  getFiscalYears: () => api.get('/api/lookups/fiscal-years'),
  getControlTypes: () => api.get('/api/lookups/control-types'),
  getRiskManagementTypes: () => api.get('/api/lookups/risk-management-types'),
};

export default api;
